// components/modals/DraftCardModal.jsx
import React, { useEffect, useState } from "react";
import "./draftCardModal.css";
import { canAccessDraft } from "../../utils/permissions";
import { getDaysRemaining } from "../../utils/dueDates";

export default function DraftCardModal({ open, onClose, draft, onAddComment, onDeleteComment, currentUser, onOpenInWriter }) {

  const [newComment, setNewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const roleMap = {
    1: "Reader",
    2: "Writer",
    3: "Editor",
    4: "Lead Editor",
    5: "Admin",
  };

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    // reset when a different draft opens
    setNewComment("");
  }, [open, draft?._id]);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const dirty = newComment.trim().length > 0;

  if (!open || !draft) return null;

  const handleAdd = async () => {
    const text = newComment.trim();
    if (!text) return;
    setSubmitting(true);
    try {
      await onAddComment?.(draft._id, text);
      setNewComment("");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setNewComment("");
  };

  return (
    <div className="dc-overlay" onClick={onClose} aria-modal="true" role="dialog">
      <div
        className="dc-modal"
        onClick={(e) => e.stopPropagation()} // prevent overlay close
        aria-label={`Draft details for ${draft.title || "Untitled"}`}
      >
        <div className="dc-header">
          <h2 id="dc-title" className="dc-title">
            {draft.title || "Untitled draft"}
          </h2>

          <div className="dc-actions">
            {canAccessDraft(draft, currentUser) && (
              <button
                className="dc-btn primary open-writer"
                onClick={() => onOpenInWriter?.(draft)}
                title="Open in The Writer’s Room"
              >
                <span className="icon">✎</span>
                <span className="label">Open in Writer’s Room</span>
              </button>
            )}

            <button className="dc-close" onClick={onClose} aria-label="Close">×</button>
          </div>
        </div>

        <div className="dc-content">
          <div className="dc-pairs">
            <div className="dc-row"><span className="dc-label">Author</span><span>{draft.authorName || "—"}</span></div>

            <div className="dc-row">
              <span className="dc-label">Created On</span>
              <span>
                {draft.createdAt
                  ? new Date(draft.createdAt).toLocaleString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : "—"}
              </span>
            </div>

            <div className="dc-row"><span className="dc-label">Editor</span><span>{draft.editorName || "—"}</span></div>

            <div className="dc-row">
              <span className="dc-label">Last Updated</span>
              <span>
                {draft.updatedAt
                  ? new Date(draft.updatedAt).toLocaleString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : "—"}
              </span>
            </div>
          </div>
          <div className="dc-row"><span className="dc-label">Genres</span>
            <span
              dangerouslySetInnerHTML={{
                __html: Array.isArray(draft.categories)
                  ? draft.categories
                      .map((c) => (typeof c === "string" ? c : c.cat_name))
                      .join(", ")
                  : draft.categories || "—",
              }}
            />
          </div>
          <div className="dc-row">
            <span className="dc-label">Meta Description</span>
            <span className="dc-body-text">{draft.metaDescription || "—"}</span>
          </div>
          <div className="dc-row">
            <span className="dc-label">Est. Word Count</span>
            <span>{draft.estWordCount || "—"}</span>
          </div>
          <div className="dc-row">
            <span className="dc-label">Stage</span>
            <span>{draft.stageTitle || "Ideation"}</span>
          </div>
          <div className="dc-row">
            <span className="dc-label">Final Draft Due</span>
            <span>
              {draft.finalDraftDue
                ? new Date(draft.finalDraftDue).toLocaleString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })
                : "—"}
            {draft.finalDraftDue && (() => {
              const remaining = getDaysRemaining(draft.finalDraftDue);
              if (!remaining) return null;
              const { text, color } = remaining;
              const background =
                      color === "green"
                        ? "rgba(0, 128, 0, 0.1)"
                        : color === "orange"
                        ? "rgba(255, 165, 0, 0.15)"
                        : "rgba(255, 0, 0, 0.1)";

              return (
                <span
                  style={{
                    color,
                    background,
                    border: `1px solid ${color}`,
                    borderRadius: "12px",
                    padding: "2px 8px",
                    marginLeft: "5px",
                    fontSize: "0.85em",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                  }}
                >
                  {text}
                </span>
              );
            })()}
            </span>
          </div>
        </div>
        <div className="dc-body">
          <div className="dc-section">
            <div className="dc-section-title">Comments</div>
            <textarea
              className="dc-notes"
              placeholder="Share a new comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              rows={5}
            />
          </div>

          <footer className="dc-footer">
            <button
              className="dc-btn ghost"
              onClick={handleCancel}
              disabled={!dirty}
            >
              Cancel
            </button>

            <button
              className="dc-btn primary"
              onClick={handleAdd}
              disabled={!dirty || submitting}
              aria-disabled={!dirty || submitting}
            >
              {submitting ? "Posting..." : "Add Comment"}
            </button>
          </footer>

          <div className="dc-comments">
            {[...(draft.comments || [])]
              .sort((a, b) => new Date(b.timestamp || b.createdAt) - new Date(a.timestamp || a.createdAt))
              .map((c) => {
                const when = c.timestamp || c.createdAt;
                const canDelete = currentUser?.role >= 4 || String(c.authorId) === String(currentUser?.id);
                return (
                  <div key={c._id} className="dc-comment">
                    <div className="dc-comment-header">
                      <span className="dc-comment-author">{c.authorName || "Unknown User"}</span>
                      {c.role && <span className="dc-comment-role">({roleMap[c.role] || "Unknown Role"}){" "}</span>}
                      {when && (
                        <span className="dc-comment-time">
                          {new Date(when).toLocaleString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      )}
                      {canDelete && (
                        <button
                          className="dc-comment-delete"
                          title="Delete comment"
                          onClick={() => onDeleteComment?.(draft._id, c._id)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                    <div className="dc-comment-body">{c.text}</div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
}
